import React, { useRef, useEffect, useMemo } from 'react';
import { gsap } from 'gsap';

const CosmicBackground = () => {
  const containerRef = useRef(null);
  const starsRef = useRef([]);
  const nebulaRef = useRef(null);
  const particlesRef = useRef([]);

  // Generate stars with varied properties
  const stars = useMemo(() => {
    return [...Array(150)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 2 + 1,
      delay: Math.random() * 5,
      duration: Math.random() * 3 + 2,
      opacity: Math.random() * 0.5 + 0.2,
    }));
  }, []);

  // Generate floating particles
  const particles = useMemo(() => {
    return [...Array(30)].map((_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 30,
      delay: Math.random() * 10,
    }));
  }, []);

  useEffect(() => {
    const nebula = nebulaRef.current;

    // Animate nebula gradient drift
    gsap.to(nebula, {
      backgroundPosition: '100% 100%',
      duration: 60,
      ease: 'none',
      repeat: -1,
      yoyo: true,
    });

    // Animate each star with twinkling effect
    starsRef.current.forEach((star, i) => {
      if (star) {
        gsap.to(star, {
          opacity: stars[i].opacity + 0.4,
          scale: 1.3,
          duration: stars[i].duration,
          delay: stars[i].delay,
          ease: 'power1.inOut',
          repeat: -1,
          yoyo: true,
        });
      }
    });

    // Animate floating particles with drift
    particlesRef.current.forEach((particle, i) => {
      if (particle) {
        gsap.to(particle, {
          x: `+=${Math.random() * 100 - 50}`,
          y: `+=${Math.random() * 100 - 50}`,
          opacity: 0.1,
          duration: particles[i].duration,
          delay: particles[i].delay,
          ease: 'none',
          repeat: -1,
          yoyo: true,
        });
      }
    });

    // Copy refs for cleanup
    const currentStars = [...starsRef.current];
    const currentParticles = [...particlesRef.current];

    return () => {
      gsap.killTweensOf(nebula);
      currentStars.forEach((star) => {
        if (star) gsap.killTweensOf(star);
      });
      currentParticles.forEach((particle) => {
        if (particle) gsap.killTweensOf(particle);
      });
    };
  }, [stars, particles]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: -10 }}
    >
      {/* Base cosmic gradient */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 50% 50%, #1a1a3e 0%, #0d0d1a 40%, #0a0a0f 100%)',
        }}
      />

      {/* Animated nebula layer */}
      <div
        ref={nebulaRef}
        className="absolute inset-0 opacity-30"
        style={{
          background: `
            radial-gradient(ellipse at 20% 20%, rgba(74, 26, 107, 0.4) 0%, transparent 50%),
            radial-gradient(ellipse at 80% 80%, rgba(45, 27, 78, 0.4) 0%, transparent 50%),
            radial-gradient(ellipse at 60% 30%, rgba(26, 26, 62, 0.6) 0%, transparent 40%)
          `,
          backgroundSize: '200% 200%',
          backgroundPosition: '0% 0%',
        }}
      />

      {/* Star field */}
      <div className="absolute inset-0">
        {stars.map((star, i) => (
          <div
            key={star.id}
            ref={(el) => (starsRef.current[i] = el)}
            className="absolute rounded-full"
            style={{
              left: `${star.x}%`,
              top: `${star.y}%`,
              width: `${star.size}px`,
              height: `${star.size}px`,
              backgroundColor: '#ffffff',
              opacity: star.opacity,
              boxShadow: `0 0 ${star.size * 2}px rgba(255, 255, 255, 0.5)`,
              willChange: 'opacity, transform',
            }}
          />
        ))}
      </div>

      {/* Floating cosmic particles */}
      <div className="absolute inset-0">
        {particles.map((particle, i) => (
          <div
            key={particle.id}
            ref={(el) => (particlesRef.current[i] = el)}
            className="absolute rounded-full"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              background: 'radial-gradient(circle, rgba(212, 175, 55, 0.3) 0%, transparent 70%)',
              opacity: 0.4,
              filter: 'blur(1px)',
              willChange: 'transform, opacity',
            }}
          />
        ))}
      </div>

      {/* Vignette overlay */}
      <div
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 30%, rgba(10, 10, 15, 0.8) 100%)',
        }}
      />

      {/* Subtle noise texture overlay */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
    </div>
  );
};

export default CosmicBackground;
